import { useNavigate } from "react-router-dom";
import { Search, Plus, Network } from "lucide-react";
import { useState } from "react";
import { useTopologies } from "../../api/hooks.js";
import { useWorldStore } from "../../stores/world.js";
import { StatusBadge } from "../common/StatusBadge.js";
import { SkeletonList } from "../common/Skeleton.js";
import { EmptyState } from "../common/EmptyState.js";

export function TopologySidebar() {
  const { data: topologies, isLoading } = useTopologies();
  const selectedTopologyId = useWorldStore((s) => s.selectedTopologyId);
  const openGallery = useWorldStore((s) => s.setTemplateGalleryOpen);
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  const filtered = topologies?.filter((t) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      t.id.toLowerCase().includes(searchLower) ||
      (t.definition?.type ?? "").toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="h-full flex flex-col bg-surface-light/40 backdrop-blur-sm">
      {/* Header */}
      <div className="px-4 pt-4 pb-2">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xs font-medium text-text-muted uppercase tracking-wider">
            Topologies
          </h2>
          <button
            onClick={() => openGallery(true)}
            title="New topology"
            className="flex items-center gap-1 rounded-md bg-brand-purple/10 px-2 py-1 text-[11px] font-medium text-brand-purple transition-[color,background-color,transform] duration-200 hover:bg-brand-purple/20 active:scale-[0.96]"
          >
            <Plus className="h-3.5 w-3.5" />
            New
          </button>
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-muted" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter..."
            className="w-full bg-surface/40 border border-border-subtle/20 rounded-lg pl-8 pr-3 py-1.5 text-xs text-text-primary placeholder-text-muted focus:outline-none focus:border-brand-purple/30 transition-colors"
          />
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto px-2 py-2">
        {isLoading ? (
          <SkeletonList count={4} />
        ) : !filtered || filtered.length === 0 ? (
          <EmptyState
            icon={Network}
            title="No topologies"
            description={search ? "No matches found" : "Spin one up from a template or build your own"}
            action={search ? undefined : { label: "New topology", onClick: () => openGallery(true) }}
          />
        ) : (
          <div className="space-y-0.5">
            {filtered.map((topo) => {
              const isSelected = selectedTopologyId === topo.id;
              const agentCount = topo.definition?.agents?.length ?? 0;
              const topoType = topo.definition?.type ?? "custom";

              return (
                <button
                  key={topo.id}
                  onClick={() => navigate(`/topology/${topo.id}`)}
                  className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-[color,background-color,transform] duration-200 active:scale-[0.98] group ${
                    isSelected
                      ? "bg-brand-purple/10 border border-brand-purple/20"
                      : "hover:bg-surface-lighter/30 border border-transparent"
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span
                      className={`font-medium truncate text-xs ${
                        isSelected ? "text-brand-purple" : "text-text-primary"
                      }`}
                    >
                      {topoType}
                    </span>
                    <StatusBadge status={topo.status} />
                  </div>
                  <div className="flex items-center gap-3 text-[11px] text-text-muted">
                    <span className="tabular-nums">{agentCount} agents</span>
                    <span className="tabular-nums">{topo.runCount} runs</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
